// Metadata settings (§11.16): Artist, Copyright, Email and Website, written into exports according to each
// export preset's metadata mode (§9). Stored in IndexedDB (not per photo).
import { useEffect, useState } from 'react';
import { useT, type MessageKey } from '../../i18n';
import { loadMetadata, saveMetadata } from '../../features/appSettings';
import type { MetadataSettings } from '../../io/encode/xmp';
import s from './Dialog.module.css';

const FIELDS: { key: keyof MetadataSettings; label: MessageKey; type: string }[] = [
  { key: 'artist', label: 'metaArtist', type: 'text' },
  { key: 'copyright', label: 'metaCopyright', type: 'text' },
  { key: 'email', label: 'metaEmail', type: 'email' },
  { key: 'website', label: 'metaWebsite', type: 'url' },
];

export function MetadataDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [m, setM] = useState<MetadataSettings | null>(null);
  useEffect(() => void loadMetadata().then(setM), []);
  if (!m) return null;
  return (
    <div
      className={s.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="meta-title"
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className={s.dialog} data-testid="metadata-dialog" style={{ maxWidth: 460 }}>
        <h2 id="meta-title">{t('metadataTitle')}</h2>
        {FIELDS.map((f) => (
          <label
            key={f.key}
            style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8, margin: '6px 0' }}
          >
            {t(f.label)}
            <input
              type={f.type}
              data-testid={`meta-${f.key}`}
              value={m[f.key] ?? ''}
              onChange={(e) => setM({ ...m, [f.key]: e.target.value })}
            />
          </label>
        ))}
        <p style={{ fontSize: 12, opacity: 0.8 }}>{t('metaHint')}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button onClick={onClose}>{t('cancel')}</button>
          <button
            data-testid="meta-save"
            style={{ background: 'var(--accent-fill)', color: 'var(--on-accent)' }}
            onClick={() => {
              const clean = Object.fromEntries(
                Object.entries(m)
                  .map(([k, v]) => [k, (v ?? '').trim()])
                  .filter(([, v]) => v),
              ) as MetadataSettings;
              void saveMetadata(clean).then(onClose);
            }}
          >
            {t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
